import React, { useState, useEffect } from 'react';

let toastId = 0;
let toastCallbacks = [];
let recentMessages = new Map();

export const showToast = (message, type = 'info', duration = 4000) => {
  const messageKey = `${message}:${type}`;
  if (recentMessages.has(messageKey)) {
    const lastShown = recentMessages.get(messageKey);
    if (Date.now() - lastShown < 2000) return;
  }
  recentMessages.set(messageKey, Date.now());
  const toast = { id: ++toastId, message, type, duration };
  toastCallbacks.forEach(callback => callback(toast));
  setTimeout(() => { recentMessages.delete(messageKey); }, 5000);
};

const toastConfig = {
  success: { bg: "#E6F7EE", border: "rgba(62,220,143,0.3)", color: "#0B1A33", shadow: "0px 3px 0px rgba(32,169,106,0.15)", icon: "\u2705" },
  error:   { bg: "#FFE9EE", border: "rgba(229,62,62,0.3)", color: "#0B1A33", shadow: "0px 3px 0px rgba(229,62,62,0.12)", icon: "\u274C" },
  warning: { bg: "#FFF8E7", border: "rgba(211,155,34,0.3)", color: "#0B1A33", shadow: "0px 3px 0px rgba(211,155,34,0.12)", icon: "\u26A0\uFE0F" },
  info:    { bg: "#EAF3FF", border: "rgba(0,101,248,0.2)", color: "#0B1A33", shadow: "0px 3px 0px rgba(0,101,248,0.10)", icon: "\u2139\uFE0F" },
};

const Toast = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const handleToast = (toast) => {
      setToasts(prev => [...prev, toast]);
      setTimeout(() => { setToasts(prev => prev.filter(t => t.id !== toast.id)); }, toast.duration);
    };

    const handleCustomToastEvent = (event) => {
      const { message, type = 'info', duration = 4000 } = event.detail;
      const messageKey = `${message}:${type}`;
      if (recentMessages.has(messageKey) && Date.now() - recentMessages.get(messageKey) < 2000) return;
      recentMessages.set(messageKey, Date.now());
      handleToast({ id: ++toastId, message, type, duration });
      setTimeout(() => { recentMessages.delete(messageKey); }, 5000);
    };

    toastCallbacks.push(handleToast);
    window.addEventListener('showToast', handleCustomToastEvent);
    return () => {
      toastCallbacks = toastCallbacks.filter(cb => cb !== handleToast);
      window.removeEventListener('showToast', handleCustomToastEvent);
    };
  }, []);

  return (
    <div className="fixed right-4 space-y-3 pointer-events-none" style={{ top: 96, zIndex: 100000 }}>
      {toasts.map((toast) => {
        const cfg = toastConfig[toast.type] || toastConfig.info;
        return (
          <div
            key={toast.id}
            className="pointer-events-auto animate-slide-in-right"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 16px",
              borderRadius: 18,
              background: cfg.bg,
              border: `2px solid ${cfg.border}`,
              color: cfg.color,
              boxShadow: cfg.shadow,
              maxWidth: 360,
              fontFamily: "'Inter', 'Fredoka', sans-serif",
              fontSize: 14,
              fontWeight: 600,
              animation: "slide-in-right 0.3s ease-out",
            }}
          >
            <span style={{ fontSize: 18, flexShrink: 0 }}>{cfg.icon}</span>
            <p style={{ flex: 1, margin: 0, whiteSpace: "pre-line", fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}>{toast.message}</p>
            <button
              onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              style={{
                flexShrink: 0, width: 28, height: 28, borderRadius: 10,
                background: "rgba(11,26,51,0.06)", border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#334155", fontSize: 14,
              }}
            >
              ✕
            </button>
          </div>
        );
      })}

      <style jsx>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default Toast;
